import instance from "~/v2/api";
import { looseArray, looseParse } from "~/v2/api/looseParse";
import {
  type BulkClosePositionsResponse,
  bulkClosePositionsResponseSchema,
  type Position,
  positionSchema,
} from "./positions.schema";

export type PositionsProduct = "live" | "paper";

export interface GetPositionsParams {
  accountID: string;
  product?: PositionsProduct;
  assetClass?: string;
  maxDTEDays?: number;
  side?: string;
  symbols?: string[];
}

export const positionsQueryKeys = {
  all: ["v3-poc", "positions"] as const,
  list: ({
    accountID,
    product,
    assetClass,
    maxDTEDays,
    side,
    symbols,
  }: GetPositionsParams) =>
    [
      ...positionsQueryKeys.all,
      accountID,
      product,
      assetClass,
      maxDTEDays,
      side,
      symbols?.slice().sort().join(","),
    ] as const,
};

export const getPositionsPath = (
  accountID: string,
  product: PositionsProduct = "paper",
): string =>
  // v2 keeps gobroker on /accounts/{id}/positions; this is the OMS-backed API.
  `/_internal/${product === "paper" ? "paper_accounts" : "accounts"}/${accountID}/positions`;

export const getPositions = async ({
  accountID,
  product = "paper",
  assetClass,
  maxDTEDays,
  side,
  symbols,
}: GetPositionsParams): Promise<Position[]> => {
  const response = await instance.get(getPositionsPath(accountID, product), {
    params: {
      ...(assetClass ? { asset_class: assetClass } : {}),
      ...(maxDTEDays !== undefined ? { max_dte_days: maxDTEDays } : {}),
      ...(side ? { side } : {}),
      ...(symbols?.length ? { symbols: symbols.join(",") } : {}),
    },
  });

  return looseParse(
    looseArray(positionSchema, "v3.getPositions"),
    response.data,
    "v3.getPositions",
  );
};

export interface ClosePositionsParams {
  accountID: string;
  product?: PositionsProduct;
  symbols: string[];
  percentage?: string;
  cancelOrders?: boolean;
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** Defensive: `looseParse` may hand back the raw payload on failure. */
const getBulkCloseFailure = (
  data: BulkClosePositionsResponse,
): string | undefined => {
  if (!isObject(data) || !Array.isArray(data.statuses)) {
    return undefined;
  }

  const failures = data.statuses.flatMap((entry) => {
    if (!isObject(entry) || !isObject(entry.status)) {
      return [];
    }

    const { code, message } = entry.status;
    if (typeof code !== "number" || code === 0) {
      return [];
    }

    const symbol = typeof entry.symbol === "string" ? entry.symbol : "position";
    const detail = typeof message === "string" ? message : `error code ${code}`;
    return [`${symbol}: ${detail}`];
  });

  return failures.length ? failures.join("; ") : undefined;
};

export const closePositions = async ({
  accountID,
  product = "paper",
  symbols,
  percentage,
  cancelOrders = false,
}: ClosePositionsParams): Promise<void> => {
  const response = await instance.delete(getPositionsPath(accountID, product), {
    data: {
      symbols,
      ...(percentage ? { percentage } : {}),
      cancel_orders: cancelOrders,
    },
  });

  const parsed = looseParse(
    bulkClosePositionsResponseSchema,
    response.data,
    "v3.closePositions",
  );
  const failure = getBulkCloseFailure(parsed);
  if (failure) {
    throw new Error(`Unable to liquidate position(s): ${failure}`);
  }
};
